import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useLanguage } from '@/contexts/LanguageContext';

export const TrainingFAQ = () => {
  const { language } = useLanguage();

  const faqs = language === 'fr' ? [
    {
      q: "La formation IA est-elle éligible à la formation continue obligatoire des avocats (FCO) ?",
      a: "Oui. Nos modules sont conçus pour répondre aux exigences de la formation continue obligatoire des avocats (20h/an). Chaque module donne lieu à une attestation de formation reconnue par le CNB et les ordres des barreaux."
    },
    {
      q: "Comment financer ma formation IA avocat ?",
      a: "Nos formations sont éligibles au financement OPCO (avocats salariés) et FIF-PL (avocats libéraux). Nous vous accompagnons dans les démarches de prise en charge. Contactez-nous pour un devis personnalisé."
    },
    {
      q: "Quels outils d'IA juridique sont couverts par la formation ?",
      a: "Nous formons sur les outils les plus adaptés à votre matière et à vos cas d'usage : solutions de recherche juridique, d'analyse documentaire, de drafting, de veille réglementaire et de conformité. Notre approche est agnostique et orientée résultats."
    },
    {
      q: "La formation est-elle disponible en webinaire ou uniquement en présentiel ?",
      a: "Les deux formats sont proposés : webinaire (à distance) et présentiel (dans vos locaux ou dans un lieu dédié). Nous proposons également des formats hybrides selon vos besoins."
    },
    {
      q: "Combien de temps dure une formation IA pour avocats ?",
      a: "Les modules FCO durent de 2h à 7h selon le niveau et la matière. Les formations ad hoc s'étendent de 1 jour à 3 mois. Les projets de solutions sur-mesure prennent de 2 à 6 mois."
    },
    {
      q: "Proposez-vous des formations par matière juridique ?",
      a: "Oui, nous proposons des modules spécialisés par matière : Private Equity & M&A, Contentieux, Corporate, Droit social, Droit fiscal, Droit public, Droit immobilier, Droit de la santé, Droit de l'urbanisme, Regulatory, Droit des sociétés et Droit pénal."
    },
    {
      q: "Qu'est-ce qu'une solution IA juridique sur-mesure ?",
      a: "Il s'agit de la conception et du déploiement d'agents IA personnalisés pour votre cabinet ou direction juridique : chatbots internes, automatisation du drafting, pipelines d'analyse documentaire, dashboards de veille réglementaire. Nous vous accompagnons de l'idée à la production."
    },
    {
      q: "La formation est-elle adaptée aux juristes d'entreprise et aux notaires ?",
      a: "Absolument. Nos programmes s'adressent à tous les professionnels du droit : avocats (salariés et libéraux), juristes d'entreprise, directions juridiques, notaires et études notariales."
    },
  ] : [
    {
      q: "Is AI training eligible for mandatory Continuing Legal Education (CLE)?",
      a: "Yes. Our modules are designed to meet mandatory CLE requirements (20h/year for French lawyers). Each module provides a recognized training certificate."
    },
    {
      q: "How can I fund my legal AI training?",
      a: "Our training programs are eligible for OPCO funding (employed lawyers) and FIF-PL (independent lawyers). We help with the administrative process. Contact us for a custom quote."
    },
    {
      q: "Which legal AI tools are covered in the training?",
      a: "We train on the tools best suited to your practice area and use cases: legal research, document analysis, drafting, regulatory watch, and compliance solutions. Our approach is tool-agnostic and results-oriented."
    },
    {
      q: "Is training available as a webinar or only on-site?",
      a: "Both formats are available: webinar (remote) and on-site (at your offices or a dedicated venue). We also offer hybrid formats depending on your needs."
    },
    {
      q: "How long does legal AI training last?",
      a: "CLE modules range from 2h to 7h depending on level and practice area. Custom training spans from 1 day to 3 months. Custom AI solution projects take 2 to 6 months."
    },
    {
      q: "Do you offer training by practice area?",
      a: "Yes, we offer specialized modules: Private Equity & M&A, Litigation, Corporate, Employment Law, Tax Law, Public Law, Real Estate, Healthcare Law, Urban Planning, Regulatory, Company Law, and Criminal Law."
    },
    {
      q: "What is a custom legal AI solution?",
      a: "It involves designing and deploying custom AI agents for your firm or legal department: internal chatbots, drafting automation, document analysis pipelines, and regulatory watch dashboards. We support you from concept to production."
    },
    {
      q: "Is the training suitable for in-house counsel and notaries?",
      a: "Absolutely. Our programs are designed for all legal professionals: lawyers (employed and independent), in-house counsel, legal departments, notaries, and notary offices."
    },
  ];

  return (
    <section className="py-20 bg-secondary/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            {language === 'fr' ? 'Questions fréquentes' : 'Frequently asked questions'}
          </h2>
          <p className="text-lg text-muted-foreground">
            {language === 'fr'
              ? 'Tout savoir sur nos formations IA pour avocats et professionnels du droit'
              : 'Everything you need to know about our AI training for legal professionals'}
          </p>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="bg-card rounded-xl border border-border px-6">
              <AccordionTrigger className="text-left text-base font-medium text-foreground hover:no-underline">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
